/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  Switch,
  Tab,
  Tabs,
  Typography
} from '@mui/material';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { useTranslation } from 'react-i18next';
import { isMobile } from 'react-device-detect';
import { useParams } from 'react-router-dom';
import useSWR from 'swr';
import ClientForm from '../../components/client/ClientForm';
import InvoiceInformation from '../../components/client/InvoiceInformation';
import '../../style/App.scss';
import { FormMainContainer } from '../../components/shared/container/containerParts';
import useUserRole from '../../hooks/useUserRoles';
import { ROLES } from '../../helpers/roles';
import useAuthStore from '../../store/zustand/auth';
import NotesClient from '../../components/client/Notes';
import ClientProjects from '../../components/clients/Projects';
import api from '../../api';
import { ClientResponseDTO } from '../../api-client/generated';
import WhitelistModal from '../serviceRequests/WhitelistModal';
import {
  mainColors,
  secondaryColors,
  successColors
} from '../../theme/themeConfig';

interface TabPanelProps {
  // eslint-disable-next-line react/require-default-props
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      aria-labelledby={`simple-tab-${index}`}
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      role="tabpanel"
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    'aria-controls': `simple-tabpanel-${index}`,
    id: `simple-tab-${index}`
  };
}

const EditClient = function EditClient() {
  const { t } = useTranslation();
  const [value, setValue] = useState(0);
  const isBusinessDevelopment = useUserRole([ROLES.BUSINESS_DEVELOPMENT]);
  const { clientId } = useParams();
  const isClient = useUserRole([ROLES.CUSTOMER]);
  const { id: profileId } = useAuthStore((state) => state);
  const [fireAPIGetClient, setFireAPIGetClient] = useState<boolean>(false);
  const [client, setClient] = useState<ClientResponseDTO>(
    {} as ClientResponseDTO
  );

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const isClientType = client?.type === 'Client';
  const [whitelistOpen, setWhitelistOpen] = useState(false);
  const [srToggleLoading, setSrToggleLoading] = useState(false);

  const clientName =
    (client as any)?.businessName ||
    `${(client as any)?.firstName ?? ''} ${
      (client as any)?.lastName ?? ''
    }`.trim() ||
    (client as any)?.email ||
    '';

  const {
    data: dataGetClient,
    error: getClientError,
    mutate: mutateCustomer
  } = useSWR(fireAPIGetClient ? clientId : null, api.ClientApi.getClient);

  const handleSrToggle = async (enabled: boolean) => {
    if (!clientId) return;
    setSrToggleLoading(true);
    try {
      await api.ClientApi.toggleServiceRequests(clientId, enabled);
      setClient(
        (prev) => ({ ...prev, serviceRequestsEnabled: enabled } as any)
      );
    } catch {
      // silently ignore
    } finally {
      setSrToggleLoading(false);
    }
  };

  useEffect(() => {
    if (dataGetClient) {
      setClient(dataGetClient as any);
      setFireAPIGetClient(false);
    }
  }, [dataGetClient]);

  useEffect(() => {
    if (getClientError) {
      setFireAPIGetClient(false);
    }
  }, [getClientError]);

  useEffect(() => {
    if (clientId) {
      setFireAPIGetClient(true);
    }
  }, []);

  return (
    <FormMainContainer isMobile={isMobile}>
      <Tabs
        aria-label="basic tabs example"
        onChange={handleChange}
        sx={{ marginBottom: '-8px', marginTop: '-8px' }}
        value={value}
      >
        <Tab
          label={t('CUSTOMER.customerInformation')}
          {...a11yProps(0)}
          value={0}
        />
        {isClientType && !isClient && !isBusinessDevelopment && (
          <Tab
            label={t('CUSTOMER.invoiceInformation')}
            {...a11yProps(1)}
            value={1}
          />
        )}
        {isClientType && !isClient && !isBusinessDevelopment && (
          <Tab label={t('MENU.cms')} {...a11yProps(2)} value={2} />
        )}
        {!isClient && <Tab label="Notes" {...a11yProps(3)} value={3} />}
        {isClientType && !isClient && !isBusinessDevelopment && (
          <Tab label="Service Requests" {...a11yProps(4)} value={4} />
        )}
      </Tabs>
      <TabPanel index={0} value={value}>
        <ClientForm
          clientId={isClient ? profileId : clientId}
          isClient={isClient}
          type="summary"
        />
      </TabPanel>
      <TabPanel index={1} value={value}>
        {clientId && <InvoiceInformation id={clientId} />}
      </TabPanel>
      <TabPanel index={2} value={value}>
        {clientId && <ClientProjects id={clientId} />}
      </TabPanel>
      <TabPanel index={3} value={value}>
        {clientId && <NotesClient id={clientId} />}
      </TabPanel>
      <TabPanel index={4} value={value}>
        {clientId && (
          <Box sx={{ maxWidth: 560 }}>
            {/* Enable toggle */}
            <Box
              sx={{
                alignItems: 'center',
                background: (client as any)?.serviceRequestsEnabled
                  ? successColors.SUCCESS_6
                  : '#F8FAFC',
                border: `1px solid ${
                  (client as any)?.serviceRequestsEnabled
                    ? successColors.SUCCESS_2
                    : secondaryColors.SECONDARY_6
                }`,
                borderRadius: '10px',
                display: 'flex',
                justifyContent: 'space-between',
                mb: '20px',
                p: '16px 20px'
              }}
            >
              <Box>
                <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>
                  Enable Service Requests
                </Typography>
                <Typography
                  sx={{
                    color: secondaryColors.SECONDARY,
                    fontSize: '12px',
                    mt: '2px'
                  }}
                >
                  Allow this client to submit ad-hoc Service Requests via email
                  or the client portal.
                </Typography>
              </Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean((client as any)?.serviceRequestsEnabled)}
                    disabled={srToggleLoading}
                    onChange={(e) => handleSrToggle(e.target.checked)}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: successColors.SUCCESS
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track':
                        {
                          backgroundColor: successColors.SUCCESS
                        }
                    }}
                  />
                }
                label=""
                sx={{ m: 0 }}
              />
            </Box>

            {/* Whitelist management — only shown when enabled */}
            {(client as any)?.serviceRequestsEnabled && (
              <>
                <Divider sx={{ mb: '20px' }} />
                <Box
                  sx={{
                    alignItems: 'flex-start',
                    display: 'flex',
                    justifyContent: 'space-between',
                    mb: '8px'
                  }}
                >
                  <Box>
                    <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>
                      Authorized Email Senders
                    </Typography>
                    <Typography
                      sx={{
                        color: secondaryColors.SECONDARY,
                        fontSize: '12px',
                        mt: '2px'
                      }}
                    >
                      Only whitelisted emails can create SRs by emailing{' '}
                      <strong>sr@mybluberry.com</strong>.
                    </Typography>
                  </Box>
                  <Button
                    onClick={() => setWhitelistOpen(true)}
                    size="small"
                    startIcon={<MailOutlineIcon />}
                    sx={{
                      '&:hover': { background: mainColors.MAIN_3 },
                      background: mainColors.MAIN,
                      color: '#fff',
                      flexShrink: 0,
                      fontSize: '12px',
                      ml: '16px'
                    }}
                    variant="contained"
                  >
                    Manage Whitelist
                  </Button>
                </Box>
              </>
            )}
          </Box>
        )}
      </TabPanel>

      {whitelistOpen && clientId && (
        <WhitelistModal
          clientId={clientId}
          clientName={clientName}
          onClose={() => setWhitelistOpen(false)}
        />
      )}
    </FormMainContainer>
  );
};

export default EditClient;
